import {
    createBrowserHistory,
    createMemoryHistory,
    History,
    Location
} from 'history'
import asyncComponent from './AsyncComponent'
import queryString from 'query-string'
import { Observable, Subject } from 'rxjs'
import { PageComponent, PageRouteDef, RouterLocation, UrlQuery } from './types'

const parseQuery = (q: string): UrlQuery =>
    // @ts-ignore
    queryString.parse(q)

const parseLocation = (l: Location): RouterLocation => ({
    path: l.pathname,
    query: parseQuery(l.search)
})

export type Options = {
    inMemory?: boolean
    onFetchPageError?: (e: Error) => void
}

export class AppRouter {
    public readonly history: History

    public pages: PageComponent[]

    private currentLoc: RouterLocation

    private locationSubj = new Subject<RouterLocation>()

    constructor(pages: PageRouteDef<any>[], opts: Options = {}) {
        this.pages = pages.map(p => ({
            path: p.path,
            comp: asyncComponent(
                p.comp,
                opts.onFetchPageError ||
                    (err => {
                        console.error('Failed to fetch async page', err)
                    })
            )
        }))
        this.history = opts.inMemory
            ? createMemoryHistory()
            : createBrowserHistory()

        this.currentLoc = parseLocation(this.history.location)
        this.history.listen(l => {
            this.currentLoc = parseLocation(l)
            this.locationSubj.next(this.currentLoc)
        })
    }

    linkTo = (p: PageRouteDef<undefined>): string => p.to(undefined)

    paramLinkTo = <Params>(p: PageRouteDef<Params>, params: Params): string =>
        p.to(params)

    push = (path: string): void => this.history.push(path)

    pushTo = (p: PageRouteDef<undefined>): void => this.push(this.linkTo(p))

    paramPushTo = <Params>(p: PageRouteDef<Params>, params: Params): void =>
        this.push(this.paramLinkTo(p, params))

    replace = (path: string): void => this.history.replace(path)

    replaceTo = (p: PageRouteDef<undefined>): void =>
        this.replace(this.linkTo(p))

    paramReplaceTo = <Params>(p: PageRouteDef<Params>, params: Params): void =>
        this.replace(this.paramLinkTo(p, params))

    getLocation = (): RouterLocation => this.currentLoc

    locationChanges = (): Observable<RouterLocation> => {
        return this.locationSubj
    }

    mergeQuery = (q: UrlQuery): void => {
        this.pushQuery({
            ...this.currentLoc.query,
            ...q
        })
    }

    pushQuery = (q: UrlQuery): void => {
        this.history.push(this.currentLoc.path + '?' + queryString.stringify(q))
    }
}
